import { GAP_REASONS, TONE } from './constants'

const SCORE_BAND_TONE = {
  strong: TONE.OK,
  moderate: TONE.WARN,
  gaps: TONE.DANGER,
  not_assessed: TONE.NEUTRAL,
}

const SECTION_PRESENTATION = {
  deployment: {
    titleId: 'AI_RISK_REPORT.SECTION.DEPLOYMENT.TITLE',
    anchor: 'deployment',
  },
  dataExposure: {
    titleId: 'AI_RISK_REPORT.SECTION.DATA_EXPOSURE.TITLE',
    anchor: 'data-exposure',
  },
  identity: {
    titleId: 'AI_RISK_REPORT.SECTION.IDENTITY.TITLE',
    anchor: 'identity-access',
  },
  thirdParty: {
    titleId: 'AI_RISK_REPORT.SECTION.THIRD_PARTY.TITLE',
    anchor: 'third-party-ai',
  },
}

const SUBSECTION_TITLE_ID = {
  copilotEligibleUsers: 'AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.TITLE',
  copilotAddOnAssigned: 'AI_RISK_REPORT.DEPLOYMENT.ADDON.TITLE',
  copilotConfiguration: 'AI_RISK_REPORT.DEPLOYMENT.COPILOT_CONFIG.TITLE',
  sharePoint: 'AI_RISK_REPORT.DATA.SHAREPOINT.TITLE',
  sensitivityDlp: 'AI_RISK_REPORT.DATA.LABELS.TITLE',
  groupGovernance: 'AI_RISK_REPORT.DATA.GROUPS.TITLE',
  guestAccess: 'AI_RISK_REPORT.DATA.GUESTS.TITLE',
  globalAdmins: 'AI_RISK_REPORT.IDENTITY.ADMINS.TITLE',
  conditionalAccess: 'AI_RISK_REPORT.IDENTITY.CA.TITLE',
  mfa: 'AI_RISK_REPORT.IDENTITY.MFA.TITLE',
  devices: 'AI_RISK_REPORT.IDENTITY.DEVICE.TITLE',
  shadowAi: 'AI_RISK_REPORT.THIRD.SHADOW_AI.TITLE',
  appsAndServicePrincipals: 'AI_RISK_REPORT.THIRD.APPS_AND_SPS.TITLE',
  tenantAuthz: 'AI_RISK_REPORT.THIRD.AUTHZ.TITLE',
}

const CAPABILITY_LABEL_ID = {
  tenantSettings: 'AI_RISK_REPORT.DATA.SHAREPOINT.STAT_RECOMMENDED_SETTINGS',
  oversharedSites: 'AI_RISK_REPORT.DATA.SHAREPOINT.STAT_OVERSHARED',
  applicationAccess: 'AI_RISK_REPORT.DATA.SHAREPOINT.STAT_APP_ACCESS',
  sensitivityLabels: 'AI_RISK_REPORT.DATA.LABELS.SENSITIVITY_CAPABILITY',
  dlp: 'AI_RISK_REPORT.DATA.LABELS.DLP_CAPABILITY',
  groupExpiration: 'AI_RISK_REPORT.DATA.GROUPS.EXPIRATION_CAPABILITY',
  dynamicGroupGuests: 'AI_RISK_REPORT.DATA.GROUPS.DYNAMIC_GUEST_CAPABILITY',
}

const GAP_REASON = {
  coming_soon: GAP_REASONS.COMING_SOON,
  license_required: GAP_REASONS.LICENSE_REQUIRED,
  data_unavailable: GAP_REASONS.NOT_ASSESSED,
}

const adaptScore = (score = {}) => ({
  value: Number.isFinite(score.value) ? score.value : null,
  tone: SCORE_BAND_TONE[score.band] ?? TONE.NEUTRAL,
})

const adaptGap = (gap) => ({
  ...gap,
  reason: GAP_REASON[gap.reason] ?? GAP_REASONS.NOT_ASSESSED,
  capabilityLabelId: CAPABILITY_LABEL_ID[gap.capabilityId],
  isPartial: gap.scope === 'capability',
})

const adaptSubsection = (subsection) => ({
  ...subsection,
  titleId: SUBSECTION_TITLE_ID[subsection.id],
  score: adaptScore(subsection.score),
  gaps: (subsection.gaps ?? []).map(adaptGap),
})

const adaptSection = (section) => {
  const presentation = SECTION_PRESENTATION[section.id]
  if (!presentation) return null

  return {
    ...section,
    ...presentation,
    score: adaptScore(section.score),
    subsections: (section.subsections ?? []).map(adaptSubsection).filter((subsection) => subsection.titleId),
  }
}

export const buildScoringPresentation = (scoring = {}) => ({
  overall: {
    current: adaptScore(scoring.overall?.current),
    afterRecommendations: adaptScore(scoring.overall?.afterRecommendations),
    afterRecommendationsAndLicenses: adaptScore(scoring.overall?.afterRecommendationsAndLicenses),
  },
  sections: (scoring.sections ?? []).map(adaptSection).filter(Boolean),
})

export const getFullSubsectionGap = (subsection) => {
  const subsectionGap = subsection.gaps.find(({ scope }) => scope === 'subsection')
  if (subsectionGap) return subsectionGap
  if (subsection.status === 'assessed' || subsection.status === 'partially_assessed') return null

  const [firstGap] = subsection.gaps
  return firstGap ? { ...firstGap, isPartial: false } : null
}

export const getPartialSubsectionGaps = (subsection) => {
  if (subsection.status !== 'partially_assessed') return []
  return subsection.gaps.filter(({ scope }) => scope === 'capability')
}

export const getControlResult = (controls, controlId) =>
  (controls ?? []).find(({ id }) => id === controlId)?.result ?? null

export const getCapabilityGap = (gaps, capabilityId) =>
  (gaps ?? []).find((gap) => gap.capabilityId === capabilityId) ?? null

export const getSubsectionView = (scoring, subsectionId) => {
  const subsection = (scoring?.sections ?? [])
    .flatMap((section) => section.subsections)
    .find(({ id }) => id === subsectionId)

  if (!subsection) throw new Error(`Missing AI risk scoring subsection: ${subsectionId}`)

  return {
    score: subsection.score,
    controls: subsection.controls,
    gap: getFullSubsectionGap(subsection),
    partialGaps: getPartialSubsectionGaps(subsection),
  }
}
